/* eslint-disable @typescript-eslint/no-explicit-any */
import { getAuthToken } from '@dynamic-labs/sdk-react-core';
import { useMutation } from '@tanstack/react-query';
import { print } from 'graphql';

type UseDatabaseMutationOptions = {
  query: any;
  variables: Record<string, any>;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
};

type UseAsyncDatabaseMutationOptions = {
  query: any;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
};

/**
 * Custom hook to perform a GraphQL mutation using React Query.
 * @function useDatabaseMutation
 * @param {UseDatabaseMutationOptions} options - The options for the GraphQL mutation.
 * @param {any} options.query - The GraphQL query document.
 * @param {Record<string, any>} options.variables - The variables for the GraphQL query.
 * @param {(data: any) => void} [options.onSuccess] - Callback function to handle success.
 * @param {(error: any) => void} [options.onError] - Callback function to handle error.
 * @returns {object} The mutation object.
 */
export const useDatabaseMutation = ({
  query,
  variables,
  onSuccess,
  onError,
}: UseDatabaseMutationOptions) => {
  const dynamicAccessToken = getAuthToken();

  const mutation = useMutation({
    mutationFn: async () => {
      return await fetch(process.env.NEXT_PUBLIC_GQL_API_URL ?? '', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${dynamicAccessToken}`,
        },
        body: JSON.stringify({
          query: print(query),
          variables: variables ?? {}, // Ensure variables is always defined
        }),
      });
    },
    onSuccess: (data) => onSuccess?.(data),
    onError: (error) => onError?.(error),
  });

  return { mutation };
};

/**
 * Custom hook to perform an asynchronous GraphQL mutation using React Query.
 * @function useAsyncDatabaseMutation
 * @param {UseAsyncDatabaseMutationOptions} options - The options for the GraphQL mutation.
 * @param {any} options.query - The GraphQL query document.
 * @param {(data: any) => void} [options.onSuccess] - Callback function to handle success.
 * @param {(error: any) => void} [options.onError] - Callback function to handle error.
 * @returns {object} The mutation object.
 */
export const useAsyncDatabaseMutation = ({
  query,
  onSuccess,
  onError,
}: UseAsyncDatabaseMutationOptions) => {
  const dynamicAccessToken = getAuthToken();

  const mutation = useMutation({
    mutationFn: async (variables: any) => {
      return await fetch(process.env.NEXT_PUBLIC_GQL_API_URL ?? '', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${dynamicAccessToken}`,
        },
        body: JSON.stringify({
          query: print(query),
          variables: variables ?? {}, // Ensure variables is always defined
        }),
      });
    },
    onSuccess: (data) => onSuccess?.(data),
    onError: (error) => onError?.(error),
  });

  return { mutation };
};
